import ArrayUtil from "./arrayUtil";
import AttributesUtil from "./attributesUtil";
import BoundsUtil from "./boundsUtil";
import BrowserUtil from "./browserUtil";
import ColorUtil from "./colorUtil";
import CriteriaUtil from "./criteriaUtil";
import DateUtil from "./dateUtil";
import DeferredUtil from "./deferredUtil";
import DistanceUnitUtil from "./distanceUnitUtil";
import EventChannel from "./EventChannel";
import FilterUtil from "./filterUtil";
import FormatterFuncs from "./formatterFuncs";
import GeomUtil from "./geom/geomUtil";
import GoogleUtil from "./googleUtil";
import HTMLUtil from "./htmlUtil";
import LegendUtil from "./legendUtil";
import LocalStorage from "./localStorage";
import MapScopeUtil from "./mapScopeUtil";
import MapUtil from "./mapUtil";
import MathUtil from "./mathUtil";
import MatrixUtil from "./matrixUtil";
import { NavUtils } from "./NavUtils";
import ObjectUtil from "./objectUtil";
import SalesforceSyncUtil from "./salesforceSyncUtil";
import SalesforceUtil from "./salesforceUtil";
import StyleUtil from "./style/styleUtil";
import TrackingEvents from "./trackingEvents";
import URLONUtil from "./URLONUtil";
import URLUtil from "./urlUtil";

import type { IEventChannel } from "../model/Models";
import type { AzureBaseMap } from "../map/module/AzureBaseMap";
import type { AzureOverlay } from "../map/module/AzureOverlay";

// the largest integer javascript can represent without a loss in precision (All javascript numbers are floating point with a 53 bit mantissa)
const MAX_SAFE_NUMBER = '9007199254740992';

const $: JQueryStatic = global.$;
const $i18n = global.$i18n;

const es = global.es; // TODO remove dependency!!!

export default class Util {
    // Number used to generate ids
    private idIndex: number;

    readonly array: ArrayUtil;
    readonly attributes: AttributesUtil;
    readonly bounds: BoundsUtil;
    readonly browser: BrowserUtil;
    readonly isCanvasSupported: boolean;
    readonly color: ColorUtil;
    readonly Counter: Function;
    readonly criteria: CriteriaUtil;
    readonly date: DateUtil;
    readonly deferred: DeferredUtil;
    readonly distanceUnit: DistanceUnitUtil;
    readonly filter: FilterUtil;
    readonly geom: GeomUtil;
    readonly google: GoogleUtil;
    readonly navUtils = NavUtils; // doesn't create new instance!!! Using class directly as all are static methods!
    readonly matrix: MatrixUtil;
    readonly HTML: HTMLUtil;
    readonly label = {
        ES_NEWLINE: "ES_NEWLINE",

        render: function(label: string) {
            // initially this will just render the ES_NEWLINE as a space
            return label ? label.replace(/ES_NEWLINE/g, " ") : label;
        }
    };
    readonly legend: LegendUtil;
    readonly localStorage: LocalStorage;
    readonly map: MapUtil;
    readonly mapScope: MapScopeUtil;
    readonly math: MathUtil;
    readonly object: ObjectUtil;
    readonly salesforce: SalesforceUtil;
    readonly salesforceSync: SalesforceSyncUtil;
    readonly style: StyleUtil;
    readonly TrackingEvents: TrackingEvents;
    readonly url: URLUtil;
    readonly URLON: URLONUtil;

    EventChannel_logging: boolean;
    EventChannel_loggingListeners: boolean;

    constructor() {
        this.idIndex = 0;

        this.EventChannel_logging = ($.sessionStorage.getItem("es_EventChannel_logging_enabled") === "true");
        if (this.EventChannel_logging) {
            console.info("Load es_EventChannel_logging_enabled from localstorage", this.EventChannel_logging);
        }
    
        this.EventChannel_loggingListeners = ($.sessionStorage.getItem("es_EventChannel_logging_listeners_enabled") === "true");
        if (this.EventChannel_loggingListeners) {
            console.info("Load es_EventChannel_logging_listeners_enabled from localstorage", this.EventChannel_loggingListeners);
        }

        // create and assign to constants the ones that are used as dependencies to other util modules
        const boundsUtil = new BoundsUtil();
        const colorUtil = new ColorUtil();
        const distanceUnitUtil = new DistanceUnitUtil();
        const matrixUtil = new MatrixUtil();
        const objectUtil = new ObjectUtil();
        const salesforceUtil = new SalesforceUtil();
        const mapUtil = new MapUtil(colorUtil, salesforceUtil);
        const mathUtil = new MathUtil();

        this.array = new ArrayUtil();
        this.attributes = new AttributesUtil();
        this.bounds = boundsUtil;
        this.browser = new BrowserUtil();

        var elem = document.createElement('canvas');
        this.isCanvasSupported = !!(elem.getContext && elem.getContext('2d'));

        this.color = colorUtil;
        this.Counter = Counter;
        this.criteria = new CriteriaUtil(boundsUtil, distanceUnitUtil, objectUtil);
        this.date = new DateUtil();
        this.deferred = new DeferredUtil();
        this.distanceUnit = distanceUnitUtil;
        this.filter = new FilterUtil();
        this.geom = new GeomUtil(boundsUtil, matrixUtil, mathUtil);
        this.google = new GoogleUtil();
        this.HTML = new HTMLUtil(this);
        this.legend = new LegendUtil(this);
        this.localStorage = new LocalStorage();
        this.matrix = matrixUtil;
        this.map = mapUtil;
        this.mapScope = new MapScopeUtil(boundsUtil, mapUtil, objectUtil);
        this.math = mathUtil;
        this.object = objectUtil;
        this.salesforce = salesforceUtil;
        this.salesforceSync = new SalesforceSyncUtil();
        this.style = new StyleUtil();
        this.TrackingEvents = new TrackingEvents(this);
        this.url = new URLUtil();
        this.URLON = new URLONUtil();

        this.createEventChannel = this.createEventChannel.bind(this);
        this.nextId = this.nextId.bind(this);
        this.isMobile = this.isMobile.bind(this);
        this.isRunningAutoTests = this.isRunningAutoTests.bind(this);
        this.isLegacyAndroid = this.isLegacyAndroid.bind(this);
        this.isLocalStorageSupported = this.isLocalStorageSupported.bind(this);
        this.isTouchScreen = this.isTouchScreen.bind(this);
        this.filterByAttr = this.filterByAttr.bind(this);
        this.attrValues = this.attrValues.bind(this);
        this.round = this.round.bind(this);
        this.buildFormatterFuncs = this.buildFormatterFuncs.bind(this);
        this.getDateString = this.getDateString.bind(this);
        this.getFileSize = this.getFileSize.bind(this);
        this.getTitleFromFilename = this.getTitleFromFilename.bind(this);
        this.getValidAttributeName = this.getValidAttributeName.bind(this);
        this.geoLocateAndZoom = this.geoLocateAndZoom.bind(this);
        this.geoLocate = this.geoLocate.bind(this);
        this.setDefaultDistanceUnit = this.setDefaultDistanceUnit.bind(this);
        this.getDefaultDistanceUnit = this.getDefaultDistanceUnit.bind(this);
        this.getNumberWithCommas = this.getNumberWithCommas.bind(this);
        this.invertValues = this.invertValues.bind(this);
        this.mailTo = this.mailTo.bind(this);
        this.getValidIntegerString = this.getValidIntegerString.bind(this);
        this.getValidDecimalString = this.getValidDecimalString.bind(this);
        this.getFeatKey = this.getFeatKey.bind(this);
        this.checkSFTimeout = this.checkSFTimeout.bind(this);
        this.copyToClipboard = this.copyToClipboard.bind(this);
        this.copyRichToClipboard = this.copyRichToClipboard.bind(this);
        this.getRandomNumber = this.getRandomNumber.bind(this);
        this.shuffleNumbers = this.shuffleNumbers.bind(this);
        this.querySelector = this.querySelector.bind(this);
        this.setAttr = this.setAttr.bind(this);
    }

    createEventChannel(validEventTypes: object, channelLabel: string): IEventChannel {
        return new EventChannel(this, validEventTypes, channelLabel);
    }

    /**
     * @function es.util.nextId Get a unique id
     *
     * @return a unique id
     */
    nextId(): string {
        return "es" + (this.idIndex++);
    }

    /**
     * @function es.util.isMobile Detect if user is on a mobile device
     *
     * @see http://stackoverflow.com/questions/3514784/best-way-to-detect-handheld-device-in-jquery
     * @note Probably not the best way of detecting this but will do for time being. Later, maybe look at: http://modernizr.com/ or something like it.
     *
     * @return true if user is on a mobile device
     */
    isMobile(): boolean {
        if (es.isMobile == null) { // null or undefined
            es.isMobile = (/iPhone|iPod|iPad|Android|IEMobile|BlackBerry/).test(navigator.userAgent);
        }

        return es.isMobile;
    }

    /**
     * 
     * @returns 
     */
    isRunningAutoTests(): boolean {
        return document.cookie.indexOf("es_running_auto_tests") !== -1;
    }

    /**
     * @function es.util.isLegacyAndroid Detect if user is on an Android 1.x, 2.x, 3.x device.
     *
     * @return true if user is on a Legacy Android mobile device e.g. Mozilla/5.0 (Windows NT 6.1; WOW64; rv:22.0) Gecko/20100101
     *         Firefox/22.015:52:46.451 e.g. Mozilla/5.0 (Linux; U; Android 2.3.5; en-gb; HTC_DesireHD_A9191 Build/GRJ90) AppleWebKit/533.1 (KHTML, like Gecko)
     *         Version/4.0 Mobile Safari/533.115:53:05.
     */
    isLegacyAndroid(): boolean {
        if (window["es"] && window["es"]["isLegacyAndroid"] == true) {
            return true;
        }

        const ua = navigator.userAgent.toLowerCase();
        const androidVersion = (ua.match(/android (\d+)/) || [undefined, undefined])[1];
        return (ua.indexOf("mobile") > -1 && androidVersion && +androidVersion <= 3);
    }

    /**
     * @function es.util.isLocalStorageSupported Detect if broswer has access to local storage
     */
    isLocalStorageSupported(): boolean {
        return isLocalStorageSupported();
    }

    querySelector(selector: string): Element | null {
        const el = document.querySelector(selector);
        if(!el){
            es.log.warn("querySelector : no matching element found!", "document.querySelector('" + selector + "')");
        }
        return el;
    }

    /**
     * Select html el with selector [selector] and set attr [attrName] with val [value] e.g,:
     *      es.util.setAttr(
     *          ".addDataset > .ui-dialog-titlebar > .ui-button"
     *          "id",
     *          "es-create-territory-area-based-close-x"
     *      );
     * @param selector
     * @param attrName note: if attr name is "class" then that class added rather than setting attr as that'd overwrite existing classes.
     * @param value
     */
    setAttr(selector: string, attrName: string, value: string): any {

        selector = selector.trim();
        attrName = attrName.trim();
        value = value.trim();

        const el = this.querySelector(selector);
        if(el) {
            if (attrName === "class") {
                el.classList.add(value);
            } else {
                el.setAttribute(attrName, value);
            }
            const elConfirm = document.querySelector( (attrName === "id" ? "#" : ".") + value);
            es.log.log(
                "es.util.setAttr",
                "set el [" + selector + "] " + attrName + " = [" + value + "]",
                [
                    {"el by selector": el},
                    {"el reselected": elConfirm}
                ]
            );
        }
    }

    /**
     * @function {boolean} isTouchScreen Is the browser touch-screen-capable? Used to identify any touchscreen (ipad, android or any other)
     */
    isTouchScreen(): boolean {
        return window.Touch || (document.documentElement && ('ontouchstart' in document.documentElement));
    }

    /**
     * 
     * @param array 
     * @param attr 
     * @param value 
     * @param fuzzy 
     * 
     * @returns 
     */
    filterByAttr(array: any[], attr: string, value: string, fuzzy: boolean): any[] {
        const ret = [];
        if (fuzzy) {
            value = value.toLowerCase();
        }

        for (let i = 0, len = array.length; i < len; i++) {
            const obj = array[i];
            if (fuzzy ? (("" + obj[attr]).toLowerCase().indexOf(value) >= 0) : (value == obj[attr])) {
                ret.push(obj);
            }
        }

        return ret;
    }

    /**
     * 
     * @param array 
     * @param attr 
     * 
     * @returns 
     */
    attrValues(array: any[], attr: string): any[] {
        const ret = new Array(array.length);
        for (let i = ret.length - 1; i >= 0; i--) {
            ret[i] = array[i][attr];
        }

        return ret;
    }

    /**
     * 
     * @param unroundedValue 
     * @param sigDigits 
     * 
     * @returns 
     */
    round(unroundedValue: number, sigDigits?: number): string {
        const value = unroundedValue.toPrecision(sigDigits ? sigDigits : 2);

        let exponent = value.indexOf("e");
        if (exponent < 0) {
            return value;
        }

        //Remove the exponential notation - it makes no sense to keep it in the context of rounding...
        const mantissa = value.substring(0, exponent);
        exponent = parseInt(value.substring(exponent + 1));

        let decimalPoint = mantissa.indexOf(".");
        if (decimalPoint < 0) {
            decimalPoint = mantissa.length;
        }

        if (exponent > 0) {
            exponent -= (mantissa.length - (decimalPoint + 1));
            const ret = [mantissa.substring(0, decimalPoint), mantissa.substring(decimalPoint + 1)];
            while (exponent-- > 0) {
                ret.push("0");
            }
            return ret.join("");
        } else {
            exponent += decimalPoint;
            const ret = ["0."];
            while (exponent++ < 0) {
                ret.push("0");
            }
            ret.push(mantissa.substring(0, decimalPoint),mantissa.substring(decimalPoint + 1));
            return ret.join("");
        }
    }

    /**
     * buildFormatterFuncs
     */
    buildFormatterFuncs(numDecimalPlaces = 2, locale: string|null, dateFormat: string): FormatterFuncs {
        return new FormatterFuncs(numDecimalPlaces, locale, dateFormat);
    }

    /**
     * 
     * @returns 
     */
    getDateString(): string {
        var now = new Date();
        var day = ("0" + now.getDate()).slice(-2);
        var month = now.getMonth();
        var monthNames = [
            "Jan", "Feb", "Mar",
            "Apr", "May", "Jun", "Jul",
            "Aug", "Sep", "Oct",
            "Nov", "Dec"
        ];
        var hour = ("0" + now.getHours()).slice(-2);
        var min = ("0" + now.getMinutes()).slice(-2);
        return  day + " " + monthNames[month] + " " + hour + ":" + min;
    }

    /**
     * Get human-readable string for ms
     * @param ms
     */
    formatMillis(ms: number): string {
        const units = [
            { name: 'd', value: 24 * 60 * 60 * 1000 },
            { name: 'h', value: 60 * 60 * 1000 },
            { name: 'm', value: 60 * 1000 },
            { name: 's', value: 1000 },
            { name: 'ms', value: 1 }
        ];

        if (ms === 0) {
            return '0ms';
        }

        const neg = ms < 0 ? '-' : '';

        const parts: string[] = [];
        let remaining = Math.abs(ms);

        for (const unit of units) {
            if (remaining >= unit.value) {
                const count = Math.floor(remaining / unit.value);
                remaining %= unit.value;
                parts.push(`${neg}${count}${unit.name}`);
            }
        }

        // Return only the most significant parts (up to 2-3 units)
        return parts.slice(0, 2).join(', ');
    }

    /**
     * Get human-readable file size for a string/byte size
     * @param size
     * @return {string}
     */
    getFileSize(size: number): string {
        const i = Math.floor( Math.log(size) / Math.log(1024) );
        return ( size / Math.pow(1024, i) ).toFixed(2) * 1 + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    /**
     * Given a string like a filename, remove the extension replace underscores with
     *     es.util.getTitleFromFilename("sunbathing-svgrepo-com.svg") === "Sunbathing-Svgrepo-Com"
     *     es.util.getTitleFromFilename("android.png" === "Android"
     * @param filename
     * @return {*}
     */
    getTitleFromFilename(filename: string) {
        if (!filename) {
            return "";
        }

        // Remove extension
        let title = filename.replace(/\.[^/.]+$/, "");

        // Replace underscores with spaces
        title = title.replace(/[_]/g, " ");

        // Convert to title case
        title = title
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

        return title;
    }

    /**
     * Given a field name, get a valid, normalised attribute name.
     */
    getValidAttributeName(name: string): string {
        const reservedWords = ["ACCESS", "ADD", "ALL", "ALTER", "AND", "ANY", "AS", "ASC", "AUDIT", "BETWEEN", "BY", "CHAR", "CHECK", "CLUSTER",
                "COLUMN", "COMMENT", "COMPRESS", "CONNECT", "CREATE", "CURRENT", "DATE", "DECIMAL", "DEFAULT", "DELETE", "DESC", "DISTINCT", "DROP", "ELSE",
                "EXCLUSIVE", "EXISTS", "FILE", "FLOAT", "FOR", "FROM", "GRANT", "GROUP", "HAVING", "IDENTIFIED", "IMMEDIATE", "IN", "INCREMENT", "INDEX",
                "INITIAL", "INSERT", "INTEGER", "INTERSECT", "INTO", "IS", "LEVEL", "LIKE", "LOCK", "LONG", "MAXEXTENTS", "MINUS", "MLSLABEL", "MODE",
                "MODIFY", "NOAUDIT", "NOCOMPRESS", "NOT", "NOWAIT", "NULL", "NUMBER", "OF", "OFFLINE", "ON", "ONLINE", "OPTION", "OR", "ORDER", "PCTFREE",
                "PRIOR", "PRIVILEGES", "PUBLIC", "RAW", "RENAME", "RESOURCE", "REVOKE", "ROW", "ROWID", "ROWNUM", "ROWS", "SELECT", "SESSION", "SET", "SHARE",
                "SIZE", "SMALLINT", "START", "SYNONYM", "SYSDATE", "TABLE", "THEN", "TO", "TRIGGER", "UID", "UNION", "UNIQUE", "UPDATE", "USER", "VALIDATE",
                "VALUES", "VARCHAR", "VARCHAR2", "VIEW", "WHENEVER", "WHERE", "WITH"];

        try {
            let rs = name;

            rs = rs.replace(/[\W_]+/g,"");

            if (rs.length > 0) {
                const c = rs.charAt(0);
                if (c >= '0' && c <= '9') {
                    rs = "X_" + rs;
                }
            }

            // We can't really match these cols due to rnd and hash used in original creation.
            // Would need to remove the randomness and use index or some sequential number
            /*
            if (rtc.length() == 0) {
                rtc = "OBJ_" + Math.round(Math.random() * Integer.MAX_VALUE);
            } else if (rtc.length() > maxLength) {
                rtc = "OBJ_" + Math.abs(rtc.hashCode());
            }
            */
            if (rs.length >= 30) {
                throw new Error("Invalid name. Field name will be longer than 30 chars which will cause problems: " + rs);
            }

            if (reservedWords.indexOf(rs) > -1) {
                es.log.info("rs " + rs + " contains keyword " + reservedWords[reservedWords.indexOf(rs)]);
                rs = rs + "_FLD";
            }

            rs = rs.toUpperCase();

            return rs;
        } catch (e) {
            // catch err or let update fail?
            // We'll let attr fail slide for now. Can investigate on case-by-case basis
            es.log.warn("ERROR parsing attr name [" + name + "]", e);
            return "ZAP_ERR_" + name;
        }
    }

    /**
     * 
     * @param baseMapModule 
     * @param overlayModule 
     * 
     * @returns 
     */
    geoLocateAndZoom(baseMapModule: AzureBaseMap, overlayModule: AzureOverlay) {
        return this.geoLocate().then((lonLatPnt) => {
            baseMapModule.setCenterAndResolution(lonLatPnt[0], lonLatPnt[1], 2.4);
            window.setTimeout(() => {
                overlayModule.pulsePoint(lonLatPnt, true, 15000);
            }, 500);
        });
    }

    /**
     * 
     * @returns 
     */
    geoLocate() {
        if (!navigator.geolocation || !navigator.geolocation.watchPosition) {
            es.error.handleUIError($i18n('_geolocate_error_notSupported'), "WARN");
            return es.util.deferred.rejected();
        }

        const dfd = $.Deferred();
        let geolocateSuccess = false;

        const watchId = navigator.geolocation.watchPosition((position) => {
            geolocateSuccess = true;
            navigator.geolocation.clearWatch(watchId);
            dfd.resolve([position.coords.longitude, position.coords.latitude]);

        }, (error) => {
            if (geolocateSuccess) {
                dfd.resolve();
                return; // clearWatch still makes a final error TIMEOUT call...
            }

            navigator.geolocation.clearWatch(watchId);

            var errorMsg = null;

            switch (error.code) {
                case error.PERMISSION_DENIED:
                    errorMsg = $i18n('_NavControls_geolocate_error_permissionDenied'); // don't report this as an err - user has refused permission
                    break;
                case error.TIMEOUT:
                    errorMsg = $i18n('_NavControls_geolocate_error_timeout'); // don't report this as an err - user has not responded to request for permission
                    break;
                case error.POSITION_UNAVAILABLE:
                    errorMsg = $i18n('_NavControls_geolocate_error_positionUnavailable');
                    break;
                default:
                    errorMsg = $i18n('_NavControls_geolocate_error_default');
                    break;
            }

            if (errorMsg !== null) {
                es.error.handleUIError(errorMsg, "WARN");
            }
            dfd.reject(errorMsg);
        }, {
            'timeout': 25000
        });

        return dfd.promise();
    }

    /**
     * 
     * @param unit 
     */
    setDefaultDistanceUnit(unit: string) {
        $.localStorage.setItem('defaultDistanceUnit', unit);
    }

    /**
     * 
     * @param returnNullIfNotSet 
     * 
     * @returns 
     */
    getDefaultDistanceUnit(returnNullIfNotSet?: boolean): string {
        var unit = $.localStorage.getItem('defaultDistanceUnit');
        if (unit == null && !returnNullIfNotSet) {
            unit = 'MILE';
        }

        return  unit;
    }

    /**
     * Get human-readable numbers with commas. For those of us who can't read 836542953 but can read 836,542,953 e.g.:
     *      es.util.getNumberWithCommas(836542953) == 836,542,953
     * @param num
     * @returns {string}
     */
    getNumberWithCommas(num: number): string {
        return num.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
    }

    /* gets an object and inverts the keys and values
    *
    */
    invertValues(json: object) {
        var ret = {};
        for (let key in json) {
            ret[json[key]] = key;
        }

        return ret;
    }

    /**
     * 
     * @param mailto 
     */
    mailTo(mailto: string) {
        if (!es.util.browser.iOS) {
            // Creates an iframe and follow mailTo link inside it, so to avoid onBeforeUnload in some browsers (TOR9-6867)
            const iframe = $('<iframe>');
            const aMailTo = $('<a href="mailto:' + mailto.replaceAll('"', '\\"') + '">email</a>');

            iframe.on('load', function() {
                iframe.contents().find('body').append(aMailTo);
                aMailTo[0].trigger('click'); // jquery click on mail to won't work http://stackoverflow.com/questions/11541408/jquery-wont-simulate-click-for-mailto-link/11541608
                // if iframe is removed immediately, click event is not processed
                window.setTimeout(() => {
                    iframe.remove();
                }, 10);
            });
            $('body').append(iframe);
        } else {
            // The iframe hack doesn't work in iOS anymore http://jira2:8080/browse/TOR9-11893
            window.top.location.href = 'mailto:' +  mailto.replaceAll('"', '\\"');
        }
    }

    /**
     * Removes left side 0s that don't affect the value (e.g. '00000' becomes '0', '0012' becomes '12).
     * @param val string representation of an integer >=0
     */
    private stripIntegerZerosPrefix(val: string): string {
        let lastZeroPrefixIndex = -1;
        for (let i = 0; i < val.length && val[i] == '0'; i++) {
            lastZeroPrefixIndex = i;
        }

        if (lastZeroPrefixIndex != -1) {
            if (lastZeroPrefixIndex == val.length - 1) { // all zeros
                val = '0';
            } else {
                val = val.substring(lastZeroPrefixIndex + 1); // throw away 0s prefix
            }
        }

        return val;
    }

    /**
     * Returns a potentially truncated integer to ensure that value is not greater than MAX_SAFE_NUMBER.
     * @param val string representation of an integer >=0 without unnecessary 0 prefixes
     */
    private getSafeIntegerString(val: string): string {
        if (val.length >= MAX_SAFE_NUMBER.length) {
            val = val.substring(0, MAX_SAFE_NUMBER.length);

            var previousWasSame = true;
            var tooBig = false;
            for (var i=0; previousWasSame && !tooBig && i < val.length; i++) {
                tooBig = previousWasSame && val.charAt(i) > MAX_SAFE_NUMBER.charAt(i);
                previousWasSame = val.charAt(i) == MAX_SAFE_NUMBER.charAt(i);
            }

            if (tooBig) {
                val = val.substring(0, val.length - 1);
            }
        }

        return val;
    }

    /**
     * Strips all invalid chars and gets an (almost always) valid integer.
     * Only invalid values it can return is '' and '-'
     */
    getValidIntegerString(val: string): string {
        val = val.replace(/[^-\d]/g, '');
        var isNeg = (val[0] == '-');

        val = val.replace(/[^\d]/g, '');
        val = this.stripIntegerZerosPrefix(val);
        val = this.getSafeIntegerString(val);

        if (isNeg && val != '0') { // no need to set val to -0
            val = '-' + val;
        }

        return val;
    }

    /**
     * Strips all invalid chars and gets an (almost always) valid decimal.
     * Only invalid values it can return is '', '-', '0.' and '-0.'
     */
    getValidDecimalString(val: string): string {
        val = val.replace(/[^-\d\.]/g, '');
        const isNeg = (val[0] == '-');

        val = val.replace(/[^\d\.]/g, '');
        val = val.split('.');
        if (val.length >= 2) {
            if (val[0] == '') {
                val[0] = '0';
            }

            if (val.length > 2) {
                const first = val.splice(0,1)[0];
                val = [first, val.join('')];
            }
        }

        val[0] = this.stripIntegerZerosPrefix(val[0]);

        const oldIntegerVal = val[0];
        val[0] = this.getSafeIntegerString(val[0]);
        if (val[0] == MAX_SAFE_NUMBER || val[0].length != oldIntegerVal.length) {
            // if max number or truncated, strip decimal part
            val = [val[0]];
        }

        val = val.join('.');
        if (isNeg) {
            val = '-' + val;
        }

        return val;
    }

    /**
     * Break a delimited featKey into an obj
     * @param featKey lvId_featId e.g. 101_202
     * @param delim optional, defaults to underscore
     * @returns {*} {lvId: 101, featId: 202}
     */
    getFeatKey(featKey: string, delim?: string) {
        if (delim == undefined) {
            delim = "_";
        }

        const split = featKey.split(delim);
        return {lvId: parseInt( split[0] ), featId: parseInt( split[1] )};
    }


    /**
     * Create promise to check session timeout in salesforce context. Always resolved in case the context is not salesforce
     *
     * @returns {*}
     */
    checkSFTimeout() {
        if (!es.isEmbeddedInSFDC) {
            return es.util.deferred.resolved();
        }
        
        return $.ajax({url: es.appUrl + 'sessionready', dataType: 'json'}).always((rs) => {
            if (this.hasSalesforceSession(rs)) {
                return es.util.deferred.resolved();
            }
            
            es.api.getEventChannel().fireEvent("onSessionTimeout", "sf_checkTimeout");
            return es.util.deferred.rejected();
        });
    }

    private hasSalesforceSession(sessionReady) {
        if (sessionReady === undefined || sessionReady.salesforce === undefined){
            return false;
        }

        return sessionReady && sessionReady.salesforce;
    }

    /**
     *
     * @param {string} text
     */
    copyToClipboard(text: string) {
        // TODO: implement a fallback method?
        navigator.clipboard.writeText(text).then(() => {
            console.log('copy to clipboard success', text);
        }, (err) => {
            console.error('copy to clipboard error', text, err);
        });
    }

    /**
     * Copy rich text to the clipboard with two flavors: text/html so rich editors
     * (Word, Outlook, Gmail) paste formatted content, and text/plain for plain-text
     * targets (Notepad). Falls back to a plain copyToClipboard if the rich Clipboard
     * API is unavailable or the write fails.
     *
     * Note: both types are copied to the clipboard together and the paste TARGET picks one,
     * so the plainText param isn't just a fallback for old browsers,  without it
     * pasting into Notepad or whatever wouldn't work.
     *
     * @param {string} html the formatted (HTML) flavor
     * @param {string} plainText the plain-text flavor
     *
     * @example
     * // Bold text in Word/Gmail, readable markdown in Notepad:
     * es.util.copyRichToClipboard('<p>The <strong>North East</strong> region</p>', 'The **North East** region');
     *
     * @example
     * // A table: Excel/Word paste real cells, plain editors get tab-separated text:
     * es.util.copyRichToClipboard(
     *     '<table><tr><td>Boston</td><td>120</td></tr></table>',
     *     'Boston\t120'
     * );
     */
    copyRichToClipboard(html: string, plainText: string) {
        if (typeof ClipboardItem === 'undefined' || !navigator.clipboard || !navigator.clipboard.write) {
            this.copyToClipboard(plainText);
            return;
        }
        const item = new ClipboardItem({
            'text/html': new Blob([html], {type: 'text/html'}),
            'text/plain': new Blob([plainText], {type: 'text/plain'})
        });
        navigator.clipboard.write([item]).then(() => {
            console.log('copy rich to clipboard success');
        }, (err) => {
            console.error('copy rich to clipboard error, falling back to plain text', err);
            this.copyToClipboard(plainText);
        });
    }

    /**
     * Inline SVG markup for the small "copy to clipboard" icon revealed on
     * hover in the feature info popup (record title and individual field rows).
     */
    copyInfoIconSvg(): string {
        return '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"'
            + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<rect x="9" y="9" width="13" height="13" rx="2"/>'
            + '<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
    }

    /**
     * Copy label/value rows to the clipboard as a rich HTML table (so Word/Excel/Gmail
     * paste real cells) plus tab-separated plain text (so Notepad etc. get readable lines).
     * @param {{label: string, value: string}[]} rows the label/value pairs to copy
     * @returns {boolean} true if something was copied
     */
    copyLabelValueRows(rows: { label: string, value: string }[]): boolean {
        if (!rows || rows.length === 0) {
            return false;
        }
        const html = '<table>' + rows.map((r) => {
            return '<tr><td><strong>' + this.HTML.encode(r.label) + '</strong></td><td>' + this.HTML.encode(r.value) + '</td></tr>';
        }).join('') + '</table>';
        const plain = rows.map((r) => {
            return r.label + '\t' + r.value;
        }).join('\n');
        this.copyRichToClipboard(html, plain);
        return true;
    }

    /**
     * Get random num between min and max  (inclusive)
     * e.g. a dice simulation would be getRandomNumber(1, 6);
     */
    getRandomNumber(min: number, max: number): number {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

    /**
     * Shuffle numbers between min and max vals (inclusive)
     * e.g. shuffleNumbers(5, 10) == [6, 10, 5, 9, 7, 8]
     */
    shuffleNumbers(min: number, max: number): number[] {
        let numbers = [];
        for (let i = min; i <= max; i++) {
            numbers.push(i);
        }

        // Shuffle the array using the Fisher-Yates algorithm
        for (let i = numbers.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
        }

        return numbers;
    }
}

/**
 * Simple counter mechanism to keep track of hits to various ids
 */
function Counter() {
    let  counter = [];

    return {
        add: function(id) {
            const urlObj = counter.find(c => c.id == id);
            if (urlObj) {
                urlObj.count++;
            } else {
                counter.push({id: id, count: 1});
            }
        },

        stats: function() {
            es.log.info(counter);
            return counter;
        }
    };
}

export function isLocalStorageSupported(): boolean {
    try {
        localStorage.setItem("es_test", "es_test");
        localStorage.removeItem("es_test");
        return 'localStorage' in window && window['localStorage'] !== null;
    } catch (e) {
        return false;
    }
}
